import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	Form,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useState } from "react"
import { Optional } from "utility-types"
import * as Validator from "yup"
import { useHasFeatureFlag } from "../Auth"
import { CopyableValue } from "./CopyableValue"
import { PlatformIcon } from "./PlatformIcon"
import { TIntegration, TNewIntegrationSchema, XHR } from "./store"

type TChannelManagerPlatform = Extract<
	Required<TIntegration>["platform"],
	"cm_djubo" | "cm_axisrooms"
>

type TNewChannelManagerIntegrationSchema = Pick<
	TNewIntegrationSchema,
	"name" | "description" | "platform"
>

const newChannelManagerIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide a description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
})

/**
 * Create an integration with a channel manager. Every channel manager is set up
 * the same way, so they share this form and only differ in what they are
 * called and in what has to be handed over to finish the connection.
 */
function NewChannelManagerIntegrationForm({
	platform,
	title,
	summary,
	handover,
	namePlaceholder,
	layout = "split",
	submitLabel,
	cancelLabel = "Cancel",
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	platform: TChannelManagerPlatform
	title: string
	summary: string
	handover: string
	namePlaceholder: string
	/** `stacked` leaves out the intro column, for callers that render their own */
	layout?: "split" | "stacked"
	submitLabel?: React.ReactNode
	cancelLabel?: React.ReactNode
	initialValues?: Optional<TNewChannelManagerIntegrationSchema>
	onSubmit: (data: TNewChannelManagerIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] =
		useState<TNewChannelManagerIntegrationSchema>(() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			platform,
		}))

	const fields = (
		<Stack gap="4">
			<TextInputField
				name="name"
				label="Integration Name"
				placeholder={namePlaceholder}
				required
			/>
			<TextAreaInputField
				name="description"
				label="Description"
				placeholder="Describe the purpose of this integration"
				required
				rows={3}
			/>
		</Stack>
	)

	return (
		<Form<TNewChannelManagerIntegrationSchema>
			initialValues={newIntegrationInitialValues}
			validate={validateFormValues(newChannelManagerIntegrationSchemaValidator)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit({ ...values })
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						{layout === "split" ? (
							<Grid gap="6">
								<Col sm={12} md={4} position="relative">
									<Stack gap="2" position="sticky" top="0">
										<Inline gap="4" alignItems="center">
											<Box>
												<PlatformIcon platform={platform} size="40" />
											</Box>
											<Heading>{title}</Heading>
										</Inline>
										<Text>
											{summary} After creating this integration, {handover}
										</Text>
									</Stack>
								</Col>
								<Col>{fields}</Col>
							</Grid>
						) : (
							fields
						)}
						{layout === "split" ? <Divider /> : null}
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting
									? "Creating Integration..."
									: submitLabel || "Create Integration"}
							</Button>
							<Button
								type="button"
								disabled={submitting}
								onClick={onCancel}
								level="tertiary"
							>
								{cancelLabel}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

type TNewChannelManagerFormProps = Omit<
	React.ComponentProps<typeof NewChannelManagerIntegrationForm>,
	"platform" | "title" | "summary" | "handover" | "namePlaceholder"
>

const DJUBO = {
	platform: "cm_djubo",
	title: "Djubo Channel Manager",
	summary:
		"Let Djubo push rates, inventory and restriction updates to your hotels.",
	handover:
		"share the webhook url and credentials with Djubo to complete the connection.",
	namePlaceholder: "e.g., Djubo Channel Manager",
} as const

const AXISROOMS = {
	platform: "cm_axisrooms",
	title: "AxisRooms Channel Manager",
	summary:
		"Let AxisRooms push rates, inventory and restriction updates to your hotels.",
	// unlike Djubo, AxisRooms authenticate with one key for all of its
	// customers, so there is nothing per-integration to hand over: it is
	// the property codes that tie its properties to the hotels here
	handover:
		"share the code of each hotel you want it to manage with AxisRooms.",
	namePlaceholder: "e.g., AxisRooms Channel Manager",
} as const

export function NewDjuboIntegrationForm(props: TNewChannelManagerFormProps) {
	return <NewChannelManagerIntegrationForm {...props} {...DJUBO} />
}

export function NewAxisRoomsIntegrationForm(
	props: TNewChannelManagerFormProps
) {
	return <NewChannelManagerIntegrationForm {...props} {...AXISROOMS} />
}

function ChannelManagerHeader({
	platform,
	title,
	children,
}: {
	platform: TChannelManagerPlatform
	title: string
	children: React.ReactNode
}) {
	return (
		<Inline gap="4" alignItems="start">
			<Box>
				<PlatformIcon platform={platform} size="42" />
			</Box>
			<Stack>
				<Heading as="h4">{title}</Heading>
				<Text>{children}</Text>
			</Stack>
		</Inline>
	)
}

/**
 * What AxisRooms needs from us once the integration exists. The webhook urls
 * are of no use to everyone, so the caller passes them only when they are to
 * be shown.
 */
export function AxisRoomsIntegration({
	urls,
}: {
	urls?: Record<string, string>
}) {
	return (
		<Stack gap="4">
			<ChannelManagerHeader
				platform={AXISROOMS.platform}
				title={AXISROOMS.title}
			>
				Let AxisRooms push rates, inventory and restriction updates to your
				hotels
			</ChannelManagerHeader>
			<Text>
				AxisRooms authenticates with a key it uses for all of its customers, so
				there are no credentials to hand over from here. It addresses a hotel by
				the code shown on the hotel's own page: share that code with AxisRooms
				for every hotel you want it to manage.
			</Text>
			{urls ? (
				// align to start so each url keep its content width instead of
				// the column stretching them
				<Stack gap="4" alignItems="start">
					<Text>
						These are the urls AxisRooms calls, one for each of its actions.
					</Text>
					{Object.entries(urls).map(([name, url]) => (
						<CopyableValue key={name} label={name} value={url} />
					))}
				</Stack>
			) : null}
		</Stack>
	)
}

type TOnboardingChannelManagerStep =
	| { name: "pick" }
	| { name: "details" }
	| { name: "handover"; integration: TIntegration }

export function OnboardingChannelManager({
	onSuccess,
	onSkip,
}: {
	onSuccess: () => void
	onSkip: () => void
}) {
	const xhr = useXHR()
	const showWebhookUrls = useHasFeatureFlag("cm_simulator")
	const [step, setStep] = useState<TOnboardingChannelManagerStep>({
		name: "pick",
	})

	if (step.name === "handover") {
		return (
			<Stack gap="8">
				<AxisRoomsIntegration
					urls={
						showWebhookUrls
							? step.integration.cm_axisrooms_webhook_config?.urls
							: undefined
					}
				/>
				<Box>
					<Button type="button" onClick={onSuccess}>
						<Icons.Ok /> Next
					</Button>
				</Box>
			</Stack>
		)
	}

	if (step.name === "details") {
		return (
			<Stack gap="8">
				<ChannelManagerHeader
					platform={AXISROOMS.platform}
					title={AXISROOMS.title}
				>
					{AXISROOMS.summary} After creating this integration,{" "}
					{AXISROOMS.handover}
				</ChannelManagerHeader>
				<Divider marginY="0" />
				<NewAxisRoomsIntegrationForm
					layout="stacked"
					initialValues={{
						name: "AxisRooms",
						description:
							"Sync rates, inventory and restrictions with the AxisRooms channel manager",
					}}
					submitLabel={
						<>
							<Icons.Ok /> Next
						</>
					}
					cancelLabel="Back"
					onSubmit={async (values) => {
						const { data: integration } = await XHR(xhr).store({
							...values,
							abilities: [],
						})
						setStep({ name: "handover", integration })
					}}
					onCancel={() => setStep({ name: "pick" })}
				/>
			</Stack>
		)
	}

	return (
		<Stack gap="8">
			<Stack gap="2">
				<Heading as="h3">Connect Channel Manager</Heading>
				<Text>
					Let your channel manager push rates, inventory and restriction updates
					to your hotels.
				</Text>
			</Stack>
			<Divider marginY="0" />
			<Box>
				<Box
					as="button"
					type="button"
					padding="4"
					borderWidth="1"
					rounded="md"
					textAlign="left"
					backgroundColor={{ default: "default", hover: "subtle" }}
					onClick={() => setStep({ name: "details" })}
				>
					<Inline gap="4" alignItems="center">
						<PlatformIcon platform={AXISROOMS.platform} size="48" />
						<Stack>
							<Heading as="h3" fontSize="md">
								{AXISROOMS.title}
							</Heading>
							<Text color="muted">
								Sync rates, inventory and restrictions with AxisRooms
							</Text>
						</Stack>
					</Inline>
				</Box>
			</Box>
			<Box>
				<Button type="button" onClick={onSkip}>
					Skip
				</Button>
			</Box>
		</Stack>
	)
}
